import { Dialog, DialogContent, DialogContentText, DialogTitle, List, ListItem } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MonthlyMachine } from '../../../../services/apiSLTP/materials/machines';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatDate } from '../../../../utils/dates';
import { crudStatus, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { getColorByPercentage, getMaterialStateColor } from '../../../../utils/colors';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import Holidays from 'date-holidays';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import dayjs from 'dayjs';
const hd = new Holidays('FR');

interface MonthlyMachinesTableProps {
    currentDate: Date;
}

const MonthlyMachinesTable: React.FC<MonthlyMachinesTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedMachine, setSelectedMachine] = useState<MonthlyMachine['dailyMachinesByDate'][number]['daily'] | null>(null);

    const { data: dataMonthlyMachines, isLoading: isMonthlyMachinesLoading } = useMonthlyMachinesQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlyMachines) return [];

        return dataMonthlyMachines.sort((a, b) => {
            if (a.machineTypeId.designation > b.machineTypeId.designation) return -1;
            if (a.machineTypeId.designation < b.machineTypeId.designation) return 1;
            return a.designation.localeCompare(b.designation);
        });
    }, [dataMonthlyMachines, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailyMachinesByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlyMachine>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) => {
                    const status = params?.data?.crudStatus;
                    if (status === crudStatus.DELETED) return { color: muiTheme.palette.error.main };
                    if (status === crudStatus.INACTIVE) return { color: muiTheme.palette.action.disabled };
                    return undefined;
                },
            },
            {
                headerName: capitalizeFirstLetter(t('type')),
                field: 'machineTypeId.designation',
                colId: 'type',
                pinned: 'left',
                initialWidth: 120,
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },
            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailyMachinesByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailyMachinesByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlyMachine>[] = daysInMonth.map(({ date, label }) => ({
            headerName: label,
            colId: label,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailyMachinesByDate.find((value) => isSameDay(new Date(value.date), date))?.daily.length,
            cellRenderer: (params: ICellRendererParams<MonthlyMachine>) => {
                const currentMachineDay = params.data?.dailyMachinesByDate.find((value) => isSameDay(new Date(value.date), date));
                return currentMachineDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentMachineDay.daily.length.toString()} stateColor={''} showDot={false} />
                        {!!currentMachineDay.daily.length && (
                            <IconButton iconName="infoSharp" onClick={() => setSelectedMachine(currentMachineDay.daily)} />
                        )}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlyMachine>[] =
            user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-machines-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlyMachinesLoading}
            />
            <Dialog open={!!selectedMachine} onClose={() => setSelectedMachine(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('sites'))}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <List>
                            {selectedMachine?.length ? (
                                selectedMachine?.map((daily, i) => (
                                    <ListItem key={i} className="gap-5px fx-r">
                                        <span className="font-w-700">{daily?.site.name}</span>
                                        <span>{'-'}</span>
                                        <span style={{ color: getMaterialStateColor(daily.dailyMachine.state) }}>
                                            {capitalizeFirstLetter(t(getMaterialStateInfos(daily.dailyMachine.state)))}
                                        </span>
                                        <span>{'-'}</span>
                                        {capitalizeFirstLetter(daily.dailyMachine.isUsed ? t('used') : t('notUsed'))}
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>{t('noSite')}</ListItem>
                            )}
                        </List>
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlyMachinesTable;
